import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, FlaskConical } from "lucide-react";
import {
  listClassificationRules,
  createClassificationRule,
  updateClassificationRule,
  deleteClassificationRule,
  testClassificationRules,
} from "@/lib/classification-rules.functions";
import { RULE_TARGET_OPTIONS, type ClassificationRule } from "@/lib/sync-categories";
import { ImportRulesCard } from "@/components/import-rules-card";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/regras")({
  head: () => ({
    meta: [{ title: "Regras de importação · Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: RulesPage,
});

type Draft = {
  id?: string;
  label: string;
  target: string;
  match_type: "keywords" | "regex";
  pattern: string;
  field: "title" | "description" | "both";
  priority: number;
  enabled: boolean;
};

const emptyDraft: Draft = {
  label: "",
  target: "ensino_manha",
  match_type: "keywords",
  pattern: "",
  field: "title",
  priority: 100,
  enabled: true,
};

const FIELD_LABEL: Record<Draft["field"], string> = {
  title: "Título",
  description: "Descrição",
  both: "Título + descrição",
};

function RuleFields({
  value,
  onChange,
  idPrefix,
}: {
  value: Draft;
  onChange: (p: Partial<Draft>) => void;
  idPrefix: string;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-label`}>Nome da regra</Label>
        <Input
          id={`${idPrefix}-label`}
          value={value.label}
          placeholder="Ex.: Estudo da tarde (variações)"
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label>Classificar como</Label>
        <Select value={value.target} onValueChange={(v) => onChange({ target: v })}>
          <SelectTrigger aria-label="Classificar como">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RULE_TARGET_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Tipo de correspondência</Label>
        <Select
          value={value.match_type}
          onValueChange={(v) => onChange({ match_type: v as Draft["match_type"] })}
        >
          <SelectTrigger aria-label="Tipo de correspondência">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="keywords">Palavras-chave (separadas por vírgula)</SelectItem>
            <SelectItem value="regex">Expressão regular (regex)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Onde procurar</Label>
        <Select value={value.field} onValueChange={(v) => onChange({ field: v as Draft["field"] })}>
          <SelectTrigger aria-label="Onde procurar">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="title">Título</SelectItem>
            <SelectItem value="description">Descrição</SelectItem>
            <SelectItem value="both">Título + descrição</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-pattern`}>
          {value.match_type === "regex" ? "Expressão regular" : "Palavras-chave"}
        </Label>
        <Textarea
          id={`${idPrefix}-pattern`}
          rows={2}
          value={value.pattern}
          placeholder={
            value.match_type === "regex"
              ? "estudo\\s+da\\s+tarde|arvit"
              : "estudo da tarde, arvit, minchá"
          }
          onChange={(e) => onChange({ pattern: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          A comparação ignora acentos e maiúsculas/minúsculas.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-priority`}>Prioridade (menor = avaliada antes)</Label>
        <Input
          id={`${idPrefix}-priority`}
          type="number"
          min={1}
          max={999}
          value={value.priority}
          onChange={(e) => onChange({ priority: Number(e.target.value) || 100 })}
        />
      </div>
      <div className="flex items-end gap-3">
        <Switch
          id={`${idPrefix}-enabled`}
          aria-label="Regra ativa"
          checked={value.enabled}
          onCheckedChange={(v) => onChange({ enabled: v })}
        />
        <Label htmlFor={`${idPrefix}-enabled`}>Regra ativa</Label>
      </div>
    </div>
  );
}

function RulesPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listClassificationRules);
  const createFn = useServerFn(createClassificationRule);
  const updateFn = useServerFn(updateClassificationRule);
  const deleteFn = useServerFn(deleteClassificationRule);
  const testFn = useServerFn(testClassificationRules);

  const { data: rules, isLoading } = useQuery({
    queryKey: ["classification-rules"],
    queryFn: () => listFn(),
  });

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [testTitle, setTestTitle] = useState("");
  const [testResult, setTestResult] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["classification-rules"] });

  const create = useMutation({
    mutationFn: (d: Draft) => createFn({ data: d }),
    onSuccess: () => {
      toast.success("Regra criada. Já vale para as próximas sincronizações.");
      setDraft(emptyDraft);
      invalidate();
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao criar regra"),
  });

  const update = useMutation({
    mutationFn: (d: Draft & { id: string }) => updateFn({ data: d }),
    onSuccess: () => {
      toast.success("Regra atualizada.");
      invalidate();
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao atualizar regra"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Regra removida.");
      invalidate();
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao remover regra"),
  });

  const test = useMutation({
    mutationFn: (title: string) => testFn({ data: { title } }),
    onSuccess: (r: any) =>
      setTestResult(
        `${r.primaryLabel}${r.festivity ? ` · festividade: ${r.festivity}` : ""}${r.matchedRule ? ` · regra: ${r.matchedRule}` : " · padrão interno"}`,
      ),
    onError: (e: any) => toast.error(e?.message ?? "Erro ao testar"),
  });

  const rowDraft = (r: ClassificationRule): Draft =>
    drafts[r.id!] ?? {
      id: r.id,
      label: r.label,
      target: r.target,
      match_type: r.match_type,
      pattern: r.pattern,
      field: r.field,
      priority: r.priority,
      enabled: r.enabled,
    };

  const setRowDraft = (r: ClassificationRule, patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [r.id!]: { ...rowDraft(r), ...patch } }));

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold text-primary">
          Regras de importação e classificação
        </h1>
        <p className="text-sm text-muted-foreground">
          Ajuste as palavras-chave e os critérios que definem quais transmissões entram no catálogo,
          e crie padrões (palavras-chave ou regex) para classificar os vídeos. Todas as mudanças
          valem para as sincronizações manuais e automáticas.
        </p>
      </header>

      <ImportRulesCard />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nova regra</CardTitle>
          <CardDescription>Adicione quantas regras precisar.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RuleFields
            value={draft}
            onChange={(p) => setDraft((d) => ({ ...d, ...p }))}
            idPrefix="new"
          />
          <Button onClick={() => create.mutate(draft)} disabled={create.isPending}>
            <Plus className="mr-2 h-4 w-4" /> Adicionar regra
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Testar classificação</CardTitle>
          <CardDescription>
            Cole um título de vídeo para ver como ele seria classificado.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            value={testTitle}
            placeholder="Ex.: Estudo da Tarde – 18ª Porção da Torah – MISHPATIM"
            onChange={(e) => setTestTitle(e.target.value)}
          />
          <Button
            variant="outline"
            onClick={() => test.mutate(testTitle)}
            disabled={!testTitle.trim() || test.isPending}
          >
            <FlaskConical className="mr-2 h-4 w-4" /> Testar
          </Button>
          {testResult && <p className="text-sm font-medium text-primary">{testResult}</p>}
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Regras cadastradas {rules ? `(${rules.length})` : ""}
        </h2>
        {isLoading && <div className="h-24 animate-pulse rounded-xl bg-secondary" />}
        {!isLoading && (rules ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nenhuma regra personalizada. A classificação usa apenas os padrões internos.
          </p>
        )}
        {(rules ?? []).map((r) => {
          const d = rowDraft(r);
          return (
            <Card key={r.id}>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">{r.label}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant={r.enabled ? "default" : "secondary"}>
                    {r.enabled ? "Ativa" : "Inativa"}
                  </Badge>
                  <Badge variant="outline">{FIELD_LABEL[r.field]}</Badge>
                  <Badge variant="outline">#{r.priority}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <RuleFields value={d} onChange={(p) => setRowDraft(r, p)} idPrefix={r.id!} />
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => update.mutate({ ...d, id: r.id! })}
                    disabled={update.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" /> Salvar
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => remove.mutate(r.id!)}
                    disabled={remove.isPending}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Remover
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>
    </div>
  );
}
